import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { activate } from "../../../../utils/keyboard-activate";
import { List, type RowComponentProps } from "react-window";
import { Dropdown } from "../../../../components/Dropdown";
import { DateTimePicker } from "../../../../components/DateTimePicker";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { useAuth } from "../../../../hooks/useAuth";
import { batchCreateCalls } from "../../../../services/calls";
import { getOrganizationMembers } from "../../../../services/calendar";
import {
  getAllMatchingProspects,
  getGradYears,
  getDefaultTagDefinitions,
} from "../../../../services/prospects";
import { getTeams } from "../../../../services/teams";
import type {
  BatchCallResult,
  OrganizationMember,
  ProspectSummaryResponse,
  TeamResponse,
} from "../../../../types/api";
import type { DefaultTagDefinition, TagFilters } from "../../../../types/tags";
import { countActiveFilters } from "../../../../types/tags";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { FilterButton } from "../players/FilterButton";
import { TagFilterModal } from "../players/TagFilterModal";
import "./CallListBuilderModal.css";

/** Matches the debounce the players toolbar uses, so typing feels the same in both places. */
const SEARCH_DEBOUNCE_MS = 300;

/** Above this, creating gets a confirm step. Calls have no bulk delete to undo with. */
const CONFIRM_THRESHOLD = 50;

/** One populated player, plus the coach assigned to call them. */
type BuilderRow = {
  prospect: ProspectSummaryResponse;
  checked: boolean;
  coachId: string | null;
  /** ISO instant for this specific call, or null until one is set. */
  scheduledAt: string | null;
};

interface CallListBuilderModalProps {
  onClose: () => void;
  /** Called after a create attempt that produced at least one call. */
  onCreated: (result: { succeededCount: number; failedCount: number }) => void;
}

const fullName = (prospect: ProspectSummaryResponse) =>
  `${prospect.firstName} ${prospect.lastName}`.trim();

/**
 * Above this many rows the list is virtualized. Below it, plain rendering keeps the DOM
 * simple and avoids absolute positioning for no benefit: a coach filtering one team is
 * usually looking at a few dozen players.
 */
const VIRTUALIZE_THRESHOLD = 100;

/** Must match the fixed row height in the stylesheet, or virtualized rows misalign. */
const ROW_HEIGHT = 56;

const VIRTUAL_LIST_HEIGHT = 420;

interface BuilderRowViewProps {
  row: BuilderRow;
  failure: BatchCallResult | undefined;
  coachOptions: { label: string; value: string }[];
  onToggle: (prospectId: string) => void;
  onCoachChange: (prospectId: string, coachId: string) => void;
  onScheduledAtChange: (prospectId: string, iso: string) => void;
  /** Absolute positioning supplied by the virtualizer; absent when rendered plainly. */
  style?: React.CSSProperties;
  /**
   * Position and set-size supplied by the virtualizer. Without these a screen reader would
   * report the row's place among the few that happen to be mounted, not among all matches.
   */
  ariaAttributes?: {
    "aria-posinset": number;
    "aria-setsize": number;
    role: "listitem";
  };
}

/**
 * One builder row. Extracted so the plain and virtualized paths render identical markup
 * rather than drifting into two lookalike implementations.
 */
function BuilderRowView({
  row,
  failure,
  coachOptions,
  onToggle,
  onCoachChange,
  onScheduledAtChange,
  style,
  ariaAttributes,
}: BuilderRowViewProps) {
  const name = fullName(row.prospect);
  const tagSummary = row.prospect.defaultTags
    .map((tag) => tag.tagValue)
    .slice(0, 3)
    .join(", ");

  return (
    <div
      className={`call-list-builder__row${
        failure ? " call-list-builder__row--failed" : ""
      }`}
      data-testid={`call-list-builder-row-${row.prospect.id}`}
      style={style}
      // activate() must come first here: ariaAttributes carries this row's
      // listitem role plus its aria-setsize/aria-posinset, and has to win over
      // the generic role="button" activate() would otherwise impose. The list
      // is virtualized, so those position attributes are the only thing telling
      // a screen reader where the row sits in the full set.
      {...activate(() => onToggle(row.prospect.id))}
      {...ariaAttributes}
    >
      <input
        type="checkbox"
        className="call-list-builder__row-check"
        data-testid={`call-list-builder-check-${row.prospect.id}`}
        checked={row.checked}
        onChange={() => onToggle(row.prospect.id)}
        // The row's own click already toggles; without this the change and the click would
        // both fire and cancel each other out.
        onClick={(event) => event.stopPropagation()}
        aria-label={`Include ${name}`}
      />

      <span className="call-list-builder__row-name">{name}</span>
      <span className="call-list-builder__row-meta">
        {row.prospect.position ?? "-"}
      </span>
      <span className="call-list-builder__row-meta">
        {row.prospect.gradYear ?? "-"}
      </span>
      <span
        className="call-list-builder__row-meta call-list-builder__row-tags"
        title={tagSummary}
      >
        {tagSummary}
      </span>

      {/* Both controls stop propagation: using them must not also toggle the row. Labels
          name the player so a screen reader can tell one row's controls from the next, and
          are hidden so the row stays compact. */}
      <div
        className="call-list-builder__row-time"
        onClick={(event) => event.stopPropagation()}
      >
        <DateTimePicker
          label={`Date and time for ${name}`}
          hideLabel
          compact
          value={row.scheduledAt}
          onChange={(iso) => onScheduledAtChange(row.prospect.id, iso)}
          placeholder="No time set"
          testId={`call-list-builder-time-${row.prospect.id}`}
        />
      </div>

      <div
        className="call-list-builder__row-coach"
        onClick={(event) => event.stopPropagation()}
      >
        <Dropdown
          label={`Coach for ${name}`}
          hideLabel
          options={coachOptions}
          value={row.coachId}
          onSelect={(value) => onCoachChange(row.prospect.id, value)}
          placeholder="Unassigned"
        />
      </div>

      {failure && (
        <span className="call-list-builder__row-failure">
          {failure.errorMessage}
        </span>
      )}
    </div>
  );
}

/** Everything a virtualized row needs, passed through react-window's `rowProps`. */
type RowRenderProps = {
  rows: BuilderRow[];
  failures: Map<string, BatchCallResult>;
  coachOptions: { label: string; value: string }[];
  onToggle: (prospectId: string) => void;
  onCoachChange: (prospectId: string, coachId: string) => void;
  onScheduledAtChange: (prospectId: string, iso: string) => void;
};

/** Adapter matching react-window's row signature. */
function VirtualBuilderRow({
  index,
  style,
  ariaAttributes,
  rows,
  failures,
  coachOptions,
  onToggle,
  onCoachChange,
  onScheduledAtChange,
}: RowComponentProps<RowRenderProps>) {
  const row = rows[index];
  return (
    <BuilderRowView
      row={row}
      failure={failures.get(row.prospect.id)}
      coachOptions={coachOptions}
      onToggle={onToggle}
      onCoachChange={onCoachChange}
      onScheduledAtChange={onScheduledAtChange}
      style={style}
      ariaAttributes={ariaAttributes}
    />
  );
}

/**
 * Builds a call list from a roster filter.
 *
 * Replaces the old one-player-at-a-time schedule modal. A coach picks a team, narrows with
 * tags and grad year, and the matching players populate with no per-player clicking. Coaches
 * are assigned in bulk with one control and overridden per row from there.
 *
 * Name search stays alongside the filters deliberately: this modal is the only way to
 * schedule a call now, so it has to serve "call this one kid" as well as "call these forty".
 *
 * Unlike its sibling call dialogs it takes no `isOpen` prop and is mounted only while open.
 * It fetches teams, coaches, tag definitions, and grad years on mount, so a self-nulling
 * always-mounted modal would run that work — and require an AuthProvider — for every render
 * of the call list. Mounting is opening, which also means initial state covers the reset.
 */
export function CallListBuilderModal({
  onClose,
  onCreated,
}: CallListBuilderModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const { effectiveOrganizationId } = useAuth();

  // Reference data
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [coaches, setCoaches] = useState<OrganizationMember[]>([]);
  const [tagDefinitions, setTagDefinitions] = useState<DefaultTagDefinition[]>([]);
  const [gradYearOptions, setGradYearOptions] = useState<number[]>([]);

  // Filters
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tagFilters, setTagFilters] = useState<TagFilters>({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Populated rows
  const [rows, setRows] = useState<BuilderRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isTruncated, setIsTruncated] = useState(false);
  const [maxRecords, setMaxRecords] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);

  // Batch settings
  const [purpose, setPurpose] = useState("");

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Map<string, BatchCallResult>>(new Map());
  const [pendingConfirm, setPendingConfirm] = useState(false);

  // Monotonic token guarding against out-of-order row responses: a slow fetch for an older
  // filter must not overwrite rows fetched for a newer one. Same pattern as CallsListSection.
  const fetchTokenRef = useRef(0);

  // Stable per attempt so a retry after a partial failure REPLAYS the calls that already
  // landed instead of creating a second copy. Rotated only after a fully clean run, since a
  // genuinely new batch must not be mistaken for a replay of the previous one.
  const runIdRef = useRef<string>(crypto.randomUUID());

  const resetAll = useCallback(() => {

    setSelectedTeamId(null);
    setSearchQuery("");
    setDebouncedSearch("");
    setTagFilters({});
    setIsFilterOpen(false);
    setRows([]);
    setTotalCount(0);
    setIsTruncated(false);
    setPurpose("");
    setError(null);
    setFailures(new Map());
    setPendingConfirm(false);
    setSubmitting(false);
    runIdRef.current = crypto.randomUUID();
  }, []);

  // A system_admin can switch organizations with this open. Every id held here — prospects
  // AND coaches — belongs to the previous tenant and must not be used for a create after the
  // switch. Same reasoning as the bulk-selection reset in PlayersSection.
  const orgIdRef = useRef(effectiveOrganizationId);
  useEffect(() => {
    if (orgIdRef.current !== effectiveOrganizationId) {
      orgIdRef.current = effectiveOrganizationId;
      resetAll();
    }
  }, [effectiveOrganizationId, resetAll]);

  // Reference data, reloaded per open so a newly created team or coach shows up.
  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      getTeams(),
      getOrganizationMembers(),
      getDefaultTagDefinitions(),
      getGradYears(),
    ]).then(([teamsResult, coachesResult, tagsResult, gradYearsResult]) => {
      if (cancelled) return;

      if (teamsResult.status === "fulfilled") setTeams(teamsResult.value);
      if (coachesResult.status === "fulfilled") setCoaches(coachesResult.value);
      if (tagsResult.status === "fulfilled") setTagDefinitions(tagsResult.value);
      if (gradYearsResult.status === "fulfilled") setGradYearOptions(gradYearsResult.value);

      // The coach list is the one that cannot degrade quietly: without it nothing can be
      // assigned, so Create would sit disabled with no explanation.
      if (coachesResult.status === "rejected") {
        console.error("Failed to load organization members:", coachesResult.reason);
        setError("Coaches could not be loaded, so calls cannot be assigned right now.");
      }
      if (teamsResult.status === "rejected") {
        console.error("Failed to load teams:", teamsResult.reason);
      }
      if (tagsResult.status === "rejected") {
        console.error("Failed to load tag definitions:", tagsResult.reason);
      }
      if (gradYearsResult.status === "rejected") {
        console.error("Failed to load grad years:", gradYearsResult.reason);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the search box so a filter fetch does not fire per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const hasAnyFilter =
    !!selectedTeamId || !!debouncedSearch.trim() || countActiveFilters(tagFilters) > 0;

  const loadRows = useCallback(async () => {
    // Nothing selected yet: an unfiltered read of a whole organization is never what the
    // coach meant on open, so wait for a first filter rather than dumping every player.
    if (!hasAnyFilter) {
      fetchTokenRef.current++;
      setRows([]);
      setTotalCount(0);
      setIsTruncated(false);
      setLoadingRows(false);
      return;
    }

    const token = ++fetchTokenRef.current;
    setLoadingRows(true);
    setError(null);

    try {
      const response = await getAllMatchingProspects({
        teamId: selectedTeamId ?? undefined,
        searchTerm: debouncedSearch.trim() || undefined,
        tagFilters: Object.keys(tagFilters).length > 0 ? tagFilters : undefined,
      });

      if (token !== fetchTokenRef.current) return;

      setRows((previous) => {
        // Carry forward per-row coach and time for players still in the result, so tweaking
        // a filter after setting them up does not throw the work away.
        const prior = new Map(previous.map((row) => [row.prospect.id, row]));
        return response.items.map((prospect) => ({
          prospect,
          checked: false,
          coachId: prior.get(prospect.id)?.coachId ?? null,
          scheduledAt: prior.get(prospect.id)?.scheduledAt ?? null,
        }));
      });
      setTotalCount(response.totalCount);
      setIsTruncated(response.isTruncated);
      setMaxRecords(response.maxRecords);
      setFailures(new Map());
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      setError(getSafeErrorMessage(err, "Failed to load players"));
      console.error("Failed to load players for the call list:", err);
    } finally {
      if (token === fetchTokenRef.current) setLoadingRows(false);
    }
  }, [hasAnyFilter, selectedTeamId, debouncedSearch, tagFilters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // ESC closes, unless a create is in flight or a confirm is showing.
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || submitting) return;
      if (pendingConfirm) {
        setPendingConfirm(false);
        return;
      }
      if (!isFilterOpen) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [submitting, pendingConfirm, isFilterOpen, onClose]);

  const coachOptions = useMemo(
    () => coaches.map((coach) => ({ label: coach.displayName, value: coach.id })),
    [coaches]
  );

  const teamOptions = useMemo(
    () => [
      { label: "All teams", value: "" },
      ...teams.map((team) => ({ label: team.name, value: team.id })),
    ],
    [teams]
  );

  const selectedTeamName = useMemo(
    () => teams.find((team) => team.id === selectedTeamId)?.name ?? null,
    [teams, selectedTeamId]
  );

  /**
   * An empty list has three different causes and a coach should not have to guess which.
   * "No players match" is actively misleading when the team simply has nobody on it yet.
   */
  const emptyMessage = useMemo(() => {
    if (!hasAnyFilter) return "Pick a team or search to get started";

    const onlyTeamSelected =
      !!selectedTeamId &&
      !debouncedSearch.trim() &&
      countActiveFilters(tagFilters) === 0;

    if (onlyTeamSelected) {
      return selectedTeamName
        ? `${selectedTeamName} has no players yet`
        : "This team has no players yet";
    }

    return "No players match these filters";
  }, [hasAnyFilter, selectedTeamId, selectedTeamName, debouncedSearch, tagFilters]);

  const checkedRows = useMemo(() => rows.filter((row) => row.checked), [rows]);
  const needCoach = checkedRows.filter((row) => !row.coachId).length;

  // A time is optional: a coach may hand out a list before anyone knows when the calls happen.
  // Those calls are created unscheduled. Only a missing coach blocks Create.
  const noTimeCount = checkedRows.filter((row) => !row.scheduledAt).length;

  /** What still blocks Create, said plainly rather than leaving a dead button. */
  const blockingHint = useMemo(() => {
    if (checkedRows.length === 0 || needCoach === 0) return null;
    return `${needCoach} ${needCoach === 1 ? "needs" : "need"} a coach`;
  }, [checkedRows.length, needCoach]);

  // Virtualize a long list, but not one showing failures: a failed row carries an extra
  // line of explanation, so it is taller than ROW_HEIGHT and fixed-height windowing would
  // clip the reason the coach needs to read. A partial run also narrows the list to just
  // the failures, so the un-virtualized path is rarely long in that state.
  const shouldVirtualize = rows.length > VIRTUALIZE_THRESHOLD && failures.size === 0;

  const canSubmit = !submitting && checkedRows.length > 0 && needCoach === 0;

  /** Sets every row, overrides included. A control labelled "assign all" that quietly skips
   *  rows would be worse than one that plainly overwrites. */
  const handleAssignAll = (coachId: string) => {
    if (!coachId) return;
    setRows((previous) => previous.map((row) => ({ ...row, coachId })));
  };

  /** Sets every row's time, overrides included, for the same reason "assign all" does. */
  const handleSetAllTimes = (iso: string) => {
    setRows((previous) => previous.map((row) => ({ ...row, scheduledAt: iso })));
  };

  const handleRowScheduledAtChange = (prospectId: string, iso: string) => {
    setRows((previous) =>
      previous.map((row) =>
        row.prospect.id === prospectId ? { ...row, scheduledAt: iso } : row
      )
    );
  };

  const handleRowCoachChange = (prospectId: string, coachId: string) => {
    setRows((previous) =>
      previous.map((row) =>
        row.prospect.id === prospectId ? { ...row, coachId: coachId || null } : row
      )
    );
  };

  const handleToggleRow = (prospectId: string) => {
    setRows((previous) =>
      previous.map((row) =>
        row.prospect.id === prospectId ? { ...row, checked: !row.checked } : row
      )
    );
  };

  const handleToggleAll = () => {
    const shouldCheckAll = checkedRows.length !== rows.length;
    setRows((previous) => previous.map((row) => ({ ...row, checked: shouldCheckAll })));
  };

  const rowKey = useCallback(
    (index: number, data: RowRenderProps) => data.rows[index].prospect.id,
    []
  );

  const rowRenderProps = useMemo<RowRenderProps>(
    () => ({
      rows,
      failures,
      coachOptions,
      onToggle: handleToggleRow,
      onCoachChange: handleRowCoachChange,
      onScheduledAtChange: handleRowScheduledAtChange,
    }),
    [rows, failures, coachOptions]
  );

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    setPendingConfirm(false);

    try {
      const response = await batchCreateCalls({
        runId: runIdRef.current,
        purpose: purpose.trim() || undefined,
        // Each row carries its own time when it has one, so no batch-level fallback is sent.
        // A row with no time omits the field entirely rather than sending null, matching the
        // optional field on the wire.
        assignments: checkedRows.map((row) => ({
          prospectId: row.prospect.id,
          assignedCoachId: row.coachId!,
          ...(row.scheduledAt ? { scheduledAt: row.scheduledAt } : {}),
        })),
      });

      const failed = new Map(
        response.results
          .filter((result) => !result.success)
          .map((result) => [result.prospectId, result])
      );
      setFailures(failed);

      onCreated({
        succeededCount: response.succeededCount,
        failedCount: response.failedCount,
      });

      if (failed.size === 0) {
        // Clean run. Rotate the key so a genuinely new batch is not read as a replay.
        runIdRef.current = crypto.randomUUID();
        onClose();
        return;
      }

      // Keep the modal open on a partial run and drop the rows that succeeded, so what is
      // left on screen is exactly what still needs attention.
      setRows((previous) => previous.filter((row) => failed.has(row.prospect.id)));
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to create calls"));
      console.error("Failed to batch create calls:", err);
    } finally {
      setSubmitting(false);
    }
  }, [purpose, checkedRows, onCreated, onClose]);

  const handleCreateClick = () => {
    if (!canSubmit) return;
    if (checkedRows.length > CONFIRM_THRESHOLD) {
      setPendingConfirm(true);
      return;
    }
    submit();
  };

  const coachNameById = useMemo(
    () => new Map(coaches.map((coach) => [coach.id, coach.displayName])),
    [coaches]
  );

  /** Distinct coaches in the batch, for the confirm step's summary. */
  const confirmBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of checkedRows) {
      if (!row.coachId) continue;
      counts.set(row.coachId, (counts.get(row.coachId) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([coachId, count]) => ({
        name: coachNameById.get(coachId) ?? "Unknown coach",
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [checkedRows, coachNameById]);

  const createLabel = submitting
    ? "Creating..."
    : `Create ${checkedRows.length} ${checkedRows.length === 1 ? "call" : "calls"}`;

  return (
    <div
      className="call-list-builder__overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div ref={dialogRef} tabIndex={-1}
        className="call-list-builder__content"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-list-builder-title"
      >
        <div className="call-list-builder__header">
          <h3 id="call-list-builder-title" className="call-list-builder__title">
            Schedule Calls
          </h3>
          <ModalCloseButton onClick={onClose} disabled={submitting} />
        </div>

        <div className="call-list-builder__body">
          <div className="call-list-builder__filters">
            <div className="call-list-builder__filter call-list-builder__filter--team">
              <Dropdown
                label="Team"
                options={teamOptions}
                value={selectedTeamId ?? ""}
                onSelect={(value) => setSelectedTeamId(value || null)}
                placeholder="All teams"
                searchable
                searchPlaceholder="Search teams..."
              />
            </div>

            <div className="call-list-builder__filter">
              <label
                className="call-list-builder__label"
                htmlFor="call-list-builder-search"
              >
                Search
              </label>
              <input
                id="call-list-builder-search"
                type="text"
                className="call-list-builder__input"
                data-testid="call-list-builder-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Player name..."
                autoComplete="off"
              />
            </div>

            <div className="call-list-builder__filter call-list-builder__filter--tags">
              <span className="call-list-builder__label">Tags</span>
              <FilterButton
                activeFilterCount={countActiveFilters(tagFilters)}
                onClick={() => setIsFilterOpen(true)}
              />
            </div>
          </div>

          <div className="call-list-builder__batch-settings">
            <div className="call-list-builder__filter">
              <label
                className="call-list-builder__label"
                htmlFor="call-list-builder-purpose"
              >
                Purpose (optional)
              </label>
              <input
                id="call-list-builder-purpose"
                type="text"
                className="call-list-builder__input"
                data-testid="call-list-builder-purpose"
                value={purpose}
                onChange={(event) => setPurpose(event.target.value)}
                placeholder="Aug 1 contact window"
                maxLength={500}
              />
            </div>
          </div>

          <div className="call-list-builder__list-header">
            <div className="call-list-builder__count-group">
              {rows.length > 0 && (
                <label className="call-list-builder__check-all">
                  <input
                    type="checkbox"
                    data-testid="call-list-builder-check-all"
                    checked={checkedRows.length === rows.length}
                    onChange={handleToggleAll}
                    aria-label="Select all players"
                  />
                </label>
              )}
              <span
                className="call-list-builder__count"
                data-testid="call-list-builder-count"
              >
                {rows.length === 0 ? emptyMessage : `${checkedRows.length} of ${rows.length} selected`}
              </span>
            </div>

            {rows.length > 0 && (
              <div className="call-list-builder__set-all">
                <div className="call-list-builder__set-all-field">
                  <DateTimePicker
                    label="Set all times"
                    value={null}
                    onChange={handleSetAllTimes}
                    placeholder="Pick a date and time..."
                    testId="call-list-builder-set-all-times"
                  />
                </div>
                <div className="call-list-builder__set-all-field">
                  <Dropdown
                    label="Assign all to"
                    options={coachOptions}
                    value={null}
                    onSelect={handleAssignAll}
                    placeholder="Select a coach..."
                  />
                </div>
              </div>
            )}
          </div>

          {isTruncated && (
            <div className="call-list-builder__notice" role="status">
              Showing the first {maxRecords.toLocaleString()} of{" "}
              {totalCount.toLocaleString()} matching players. Narrow the filters to see the
              rest.
            </div>
          )}

          {error && (
            <div className="call-list-builder__error" role="alert">
              {error}
            </div>
          )}

          {failures.size > 0 && (
            <div className="call-list-builder__notice" role="alert">
              {failures.size} {failures.size === 1 ? "call" : "calls"} could not be created.
              The rest were created and removed from this list.
            </div>
          )}

          <div className="call-list-builder__rows" data-testid="call-list-builder-rows">
            {loadingRows && (
              <div className="call-list-builder__status">Loading players...</div>
            )}

            {!loadingRows && shouldVirtualize && (
              <List
                className="call-list-builder__virtual"
                rowComponent={VirtualBuilderRow}
                rowCount={rows.length}
                rowHeight={ROW_HEIGHT}
                rowKey={rowKey}
                rowProps={rowRenderProps}
                style={{ height: VIRTUAL_LIST_HEIGHT }}
              />
            )}

            {!loadingRows &&
              !shouldVirtualize &&
              rows.map((row) => (
                <BuilderRowView
                  key={row.prospect.id}
                  row={row}
                  failure={failures.get(row.prospect.id)}
                  coachOptions={coachOptions}
                  onToggle={handleToggleRow}
                  onCoachChange={handleRowCoachChange}
                  onScheduledAtChange={handleRowScheduledAtChange}
                />
              ))}
          </div>
        </div>

        <div className="call-list-builder__footer">
          {blockingHint && (
            <span className="call-list-builder__hint" data-testid="call-list-builder-hint">
              {blockingHint}
            </span>
          )}
          {!blockingHint && noTimeCount > 0 && (
            // Informational, not blocking: say plainly that these will be created without a
            // time so it never looks like the picker silently failed.
            <span
              className="call-list-builder__note"
              data-testid="call-list-builder-no-time-note"
            >
              {noTimeCount} {noTimeCount === 1 ? "call has" : "calls have"} no time yet
            </span>
          )}
          <button
            type="button"
            className="call-list-builder__cancel-button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="call-list-builder__save-button"
            data-testid="call-list-builder-submit"
            onClick={handleCreateClick}
            disabled={!canSubmit}
          >
            {createLabel}
          </button>
        </div>

        {pendingConfirm && (
          <div ref={dialogRef} tabIndex={-1} className="call-list-builder__confirm" role="alertdialog" aria-modal="true">
            <div className="call-list-builder__confirm-content">
              <h4 className="call-list-builder__confirm-title">
                Create {checkedRows.length} calls?
              </h4>
              <p className="call-list-builder__confirm-body">
                There is no bulk delete for calls, so removing them means doing it one at a
                time.
              </p>
              <ul className="call-list-builder__confirm-list">
                {confirmBreakdown.map((entry) => (
                  <li key={entry.name}>
                    {entry.name}: {entry.count}
                  </li>
                ))}
              </ul>
              <div className="call-list-builder__confirm-actions">
                <button
                  type="button"
                  className="call-list-builder__cancel-button"
                  onClick={() => setPendingConfirm(false)}
                  data-testid="call-list-builder-confirm-cancel"
                >
                  Go back
                </button>
                <button
                  type="button"
                  className="call-list-builder__save-button"
                  onClick={submit}
                  data-testid="call-list-builder-confirm-create"
                >
                  Create {checkedRows.length} calls
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <TagFilterModal
        isOpen={isFilterOpen}
        definitions={tagDefinitions}
        gradYearOptions={gradYearOptions}
        currentFilters={tagFilters}
        onApply={(filters) => {
          setTagFilters(filters);
          setIsFilterOpen(false);
        }}
        onReset={() => {
          setTagFilters({});
          setIsFilterOpen(false);
        }}
        onClose={() => setIsFilterOpen(false)}
      />
    </div>
  );
}
